import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { ParsedTransaction } from "@stepfinance/transaction-history";
import { useInfiniteQuery } from "@tanstack/react-query";
import shallow from "zustand/shallow";

import SteppyHand from "../../../assets/steppy-ok-hand.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { notify } from "../../../utils/notifications";
import { STEP_API_ENDPOINT } from "../../../utils/swr";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { isUserTransactionParsedTx } from "../../../utils/transaction-history/utils";
import { fetchAndCacheMetaplexMetadata, fetchJSONFromUrl } from "../../../utils/utils";
import { useStepAuthStore } from "../../stores/stepAuth";
import type { TransactionsFilters } from "../../stores/transactionHistoryStore";
import { useTransactionHistoryStore } from "../../stores/transactionHistoryStore";
import { useTransactionCacheStatus } from "./useTransactionCacheStatus";

export interface LockedTransactions {
  remainingTxCount: number;
}

export type UserTransaction = ParsedTransaction | LockedTransactions;

// export type TransactionList = [LockedTransactions | ParsedTransaction, ...Array<ParsedTransaction>] | [];

export const isLockedTransactionsRow = (row: LockedTransactions | ParsedTransaction): row is LockedTransactions => {
  return (row as LockedTransactions).remainingTxCount !== undefined;
};

export const useTransactionHistoryQuery = (wallet?: string, year?: number | string, hasTier?: boolean) => {
  const {
    currentLanguageData: { common },
  } = useLanguageContext();
  const { connection } = useConnection();
  const [jwt] = useStepAuthStore((s) => [s.jwt]);
  const { data: transactionCacheStatus } = useTransactionCacheStatus();
  const JSONfilters = useTransactionHistoryStore((s) => JSON.stringify(s.filters), shallow);

  return useInfiniteQuery({
    queryKey: ["tx-history", wallet, year, JSONfilters, jwt],
    queryFn: ({ pageParam = 1, queryKey }) => fetchTransactionHistoryQuery(connection, queryKey, pageParam, !!hasTier),
    retry: 4,
    retryDelay: (attemptNumber: number) => {
      if (attemptNumber === 4) {
        notify({
          icon: <img src={SteppyHand} width={40} alt="step-guy" />,
          type: "info",
          message: common.notifications.transactionHistoryLoading,
        });
      }
      // Apply exponential backoff for every retry after 3 retries, max out at 1 minute between retries.
      return Math.min(attemptNumber > 3 ? 2 ** attemptNumber * 1000 : 2500, 60 * 1000);
    },
    enabled: Boolean(Boolean(wallet) && transactionCacheStatus?.initialScanComplete && jwt) && !!hasTier,
    refetchOnWindowFocus: false,
    onError() {
      notify({ type: "error", message: `${common.notifications.transactionHistoryError.message}`, duration: 10 });
    },
    getNextPageParam: (lastPage, allPages) => {
      // we set a default of 35 items per page as fallback
      // if filter page size goes undefined
      const filters = JSON.parse(JSONfilters) as TransactionsFilters;
      const itemsPerPage = filters.itemsPerPage || 35;
      if (lastPage.length < itemsPerPage || !jwt) {
        return false;
      }
      return allPages.length + 1;
    },
  });
};

const fetchTransactionHistoryQuery = async (
  connection: Connection,
  queryKey: (string | number | Omit<TransactionsFilters, "page"> | undefined | null)[],
  page: number,
  hasTier: boolean,
) => {
  const wallet = queryKey[1] as string | undefined;
  const year = queryKey[2] as string | number | undefined;
  const filters = JSON.parse(queryKey[3] as string) as TransactionsFilters;
  const jwt = queryKey[4] as string;
  if (!jwt || !hasTier) {
    return [];
  }
  filters.page = page;
  const data: UserTransaction[] = await fetchJSONFromUrl(
    `${STEP_API_ENDPOINT}/v1/transaction-history/list?pubkey=${wallet}&year=${year}`,
    {
      method: "POST",
      body: JSON.stringify({ filters }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `bearer ${jwt}`,
      },
    },
  );

  const unknownMints = new Set<string>();
  for (const tx of data) {
    if (isUserTransactionParsedTx(tx)) {
      const sentData = Object.keys(tx.metadata.parsedBalanceChanges.sent);
      for (const key of sentData) {
        if (!getTokenByMint(key)) {
          unknownMints.add(key);
        }
      }

      const receivedData = Object.keys(tx.metadata.parsedBalanceChanges.recieved);
      for (const key of receivedData) {
        if (!getTokenByMint(key)) {
          unknownMints.add(key);
        }
      }
    }
  }

  await fetchAndCacheMetaplexMetadata(
    connection,
    Array.from(unknownMints.values()).map((k) => new PublicKey(k)),
    false,
  );

  return data;
};
